import { useQuery } from '@tanstack/react-query'
import { Pencil, Printer, ShoppingCart } from 'lucide-react'
import type { ReactNode } from 'react'

import { formatMoney, MONEY_SCALE, parseHumanAmount } from '@aeris/money'
import { formatDocumentNumber } from '@aeris/printing'
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  EmptyState,
  QueryState,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@aeris/ui'

import { fetchPurchase } from '../lib/api.ts'
import { formatDate } from '../lib/date-format.ts'
import { PURCHASE_STATUS_META } from '../lib/purchasing-status-meta.tsx'
import { labelsFromPurchase, type LabelDemand } from '../lib/received-labels.ts'

import { DocumentStatusBadge } from './status-badge.tsx'

/**
 * A ficha da compra — F154.
 *
 * Clicar na linha abre isto, e não o formulário de edição. Em Compras isso
 * muda mais do que nas outras telas: **quem tem só `purchasing.read` não tinha
 * clique nenhum** — a coluna de ações só existe para quem pode escrever ou
 * receber, então o gerente que quisesse conferir o frete de uma nota não tinha
 * por onde. Esta ficha é a porta que faltava.
 *
 * E há bastante para mostrar. A tabela tem sete colunas; a compra tem frete,
 * desconto, subtotal, previsão de entrega, quando foi confirmada, quando foi
 * recebida, a observação, o motivo do cancelamento — e **as linhas**, que só
 * existiam dentro do formulário de edição e do recebimento, os dois lugares
 * onde qualquer tecla muda a compra.
 *
 * As linhas pedem uma consulta (a lista não as carrega), e por isso este
 * diálogo recebe `purchaseId` e busca por dentro, no molde do
 * `SalesDocumentDialog`.
 */

const brl = (wire: string): string => {
  const amount = parseHumanAmount(wire.replace('.', ','), MONEY_SCALE)
  return amount === null ? wire : formatMoney(amount)
}

/** `2,5` e não `2.500000`. */
const quantity = (wire: string): string =>
  wire.replace(/\.?0+$/, '').replace('.', ',')

const Line = ({
  label,
  children,
}: {
  label: string
  children: ReactNode
}): ReactNode => (
  <>
    <dt className="text-(--text-muted)">{label}</dt>
    <dd className="text-(--text-primary)">{children}</dd>
  </>
)

export const PurchaseDetailDialog = ({
  purchaseId,
  onClose,
  onEdit,
  onPrintLabels,
  printingLabels = false,
}: {
  purchaseId: string
  onClose: () => void
  /**
   * Abrir o formulário de edição a partir daqui.
   *
   * **Quem decide se pode editar é a TELA, não este diálogo** — em Compras a
   * regra é `purchasing.write` **e** situação `draft`, porque uma compra
   * confirmada já virou custo e estoque a receber. Repetir a regra aqui seria
   * mantê-la em dois lugares e descobrir a divergência no dia em que só um dos
   * dois for corrigido.
   *
   * Ausente, o botão não existe.
   */
  onEdit?: (() => void) | undefined
  /**
   * Pôr na fila as etiquetas do que já chegou (F195) — a compra recebida ontem
   * também vira etiqueta, e não só a que acabou de ser recebida.
   *
   * Como `onEdit`, **a tela decide** se existe (quem não lê o catálogo não
   * busca preço para etiqueta). O diálogo só decide o que é dele: sem nada
   * recebido, não há o que etiquetar e o botão não aparece.
   */
  onPrintLabels?: ((demand: readonly LabelDemand[]) => void) | undefined
  /** A fila está sendo montada — o botão espera. */
  printingLabels?: boolean
}): ReactNode => {
  const query = useQuery({
    queryKey: ['purchase', purchaseId],
    queryFn: ({ signal }) => fetchPurchase(purchaseId, signal),
  })

  const document = query.data?.document ?? null
  const labelDemand =
    document === null ? [] : labelsFromPurchase(document.lines)
  const labelTotal = labelDemand.reduce((sum, item) => sum + item.labels, 0)

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent
        title={
          document === null
            ? 'Compra'
            : `Compra ${formatDocumentNumber(document.number)}`
        }
        description="Conferir a compra. Nada muda nesta tela: alterar é pelo botão Editar."
      >
        <QueryState
          isPending={query.isPending}
          isError={query.isError}
          error={query.error}
          onRetry={() => {
            void query.refetch()
          }}
          what="a compra"
          isEmpty={document === null}
          empty={
            <EmptyState
              icon={<ShoppingCart aria-hidden="true" className="h-8 w-8" />}
              title="Compra não encontrada"
              description="Ela pode ter sido apagada enquanto esta lista estava aberta."
            />
          }
        >
          {document === null ? null : (
            <div className="flex flex-col gap-4">
              <div className="flex flex-wrap items-center gap-2">
                <DocumentStatusBadge
                  meta={PURCHASE_STATUS_META[document.status]}
                />
                <span className="text-(length:--text-sm) text-(--text-secondary)">
                  {document.branchName}
                </span>
              </div>
              {/* A explicação em texto: na ficha há espaço, e depender de
                  hover deixaria de fora quem usa teclado ou celular (F156). */}
              <p className="-mt-2 text-(length:--text-xs) text-(--text-secondary)">
                {PURCHASE_STATUS_META[document.status].what}
              </p>

              <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-(length:--text-sm)">
                <Line label="Fornecedor">{document.supplierName}</Line>
                {/* O número DELES, o que está impresso na nota que chegou
                    junto com a mercadoria — é por ele que o financeiro
                    reencontra a compra. */}
                <Line label="Nota do fornecedor">
                  {document.supplierDocumentNumber ?? '—'}
                </Line>
                <Line label="Comprador">{document.buyerName ?? '—'}</Line>
                <Line label="Emissão">{formatDate(document.issuedAt)}</Line>
                <Line label="Previsão de entrega">
                  {document.expectedAt === null
                    ? '—'
                    : formatDate(document.expectedAt)}
                </Line>
                {document.confirmedAt === null ? null : (
                  <Line label="Confirmada em">
                    {formatDate(document.confirmedAt)}
                  </Line>
                )}
                {document.receivedAt === null ? null : (
                  <Line label="Recebida em">
                    {formatDate(document.receivedAt)}
                  </Line>
                )}
                {document.notes === null ? null : (
                  <Line label="Observação">{document.notes}</Line>
                )}
                {document.cancellationReason === null ? null : (
                  // Por que foi cancelada fica à vista: é a pergunta que alguém
                  // faz semanas depois, e a lista só mostra que foi.
                  <Line label="Motivo do cancelamento">
                    {document.cancellationReason}
                  </Line>
                )}
              </dl>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>Item</TableHeaderCell>
                      <TableHeaderCell numeric>Qtd.</TableHeaderCell>
                      {/* Quanto já chegou: uma compra recebida pela metade não
                          se distingue de uma inteira em lugar nenhum da lista. */}
                      <TableHeaderCell numeric>Recebido</TableHeaderCell>
                      <TableHeaderCell numeric>Custo un.</TableHeaderCell>
                      <TableHeaderCell numeric>Total</TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {document.lines.map((line) => (
                      <TableRow key={line.id}>
                        <TableCell className="text-(--text-primary)">
                          {line.description}
                          {line.supplierCode === null ? null : (
                            <span className="ml-2 font-(--font-mono) text-(length:--text-xs) text-(--text-muted)">
                              {line.supplierCode}
                            </span>
                          )}
                        </TableCell>
                        <TableCell numeric>
                          {quantity(line.quantity)} {line.unit}
                        </TableCell>
                        <TableCell numeric>
                          {quantity(line.receivedQuantity)}
                        </TableCell>
                        <TableCell numeric>{brl(line.unitCost)}</TableCell>
                        <TableCell numeric>{brl(line.lineTotal)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <dl className="grid grid-cols-2 gap-x-4 gap-y-1 self-end text-(length:--text-sm)">
                <Line label="Subtotal">{brl(document.subtotalAmount)}</Line>
                <Line label="Desconto">{brl(document.discountAmount)}</Line>
                {/* O frete fica no cabeçalho, não diluído nos custos unitários
                    (ADR-0020). Mostrá-lo separado é o que permite conferir o
                    total contra a nota do fornecedor. */}
                <Line label="Frete">{brl(document.freightAmount)}</Line>
                <dt className="font-(--weight-semibold) text-(--text-primary)">
                  Total
                </dt>
                <dd className="font-(--weight-semibold) text-(--text-primary)">
                  {brl(document.totalAmount)}
                </dd>
              </dl>
            </div>
          )}
        </QueryState>

        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Fechar
          </Button>
          {onPrintLabels === undefined || labelTotal === 0 ? null : (
            <Button
              variant="secondary"
              loading={printingLabels}
              leadingIcon={<Printer aria-hidden="true" className="h-4 w-4" />}
              onClick={() => {
                onPrintLabels(labelDemand)
              }}
            >
              {`Etiquetas do que chegou (${String(labelTotal)})`}
            </Button>
          )}
          {onEdit === undefined ? null : (
            <Button
              variant="primary"
              leadingIcon={<Pencil aria-hidden="true" className="h-4 w-4" />}
              onClick={onEdit}
            >
              Editar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
